import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    ArrowDropUp as ArrowDropUpIcon,
    ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';

const CHART_COLORS = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe',
    '#00C49F', '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57',
    '#8dd1e1', '#83a6ed', '#e6194b', '#3cb44b', '#ffe119',
];

const chartMetrics = [
    { value: 'ordersCount', label: 'Заказы' },
    { value: 'unitsCount', label: 'Штуки' },
    { value: 'avgPrice', label: 'Ср. цена' },
    { value: 'profit', label: 'Профит' },
    { value: 'ordersSumRub', label: 'Сумма заказов' },
];

const breakdowns = [
    { value: 'gruppa', label: 'Группа' },
    { value: 'podgruppa', label: 'Подгруппа' },
    { value: 'article', label: 'Товар' },
];

export default function AnalyticsPivot() {
    const { error: showError } = useNotify();

    const formatYmd = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const today = new Date();
    const minus7 = new Date(today);
    minus7.setDate(today.getDate() - 7);

    const [filters, setFilters] = useState({});
    const [breakdown, setBreakdown] = useState('podgruppa');
    const [dateFrom, setDateFrom] = useState(formatYmd(minus7));
    const [dateTo, setDateTo] = useState(formatYmd(today));
    const [rawRows, setRawRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDelta, setShowDelta] = useState(true);
    const [chartMetric, setChartMetric] = useState('ordersCount');
    const [chartEntities, setChartEntities] = useState([]);
    const [showTotal, setShowTotal] = useState(true);

    const toggleChartEntity = (entity) => {
        setChartEntities(prev => {
            const next = prev.includes(entity) ? prev.filter(e => e !== entity) : [...prev, entity];
            if (next.length > 0 && showTotal) setShowTotal(false);
            if (next.length === 0) setShowTotal(true);
            return next;
        });
    };

    const cellStyle = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

    const fmtNum = (v, decimals = 0) => {
        if (v == null || v === '') return '-';
        const n = Number(v);
        if (isNaN(n)) return '-';
        return n.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };
    const fmtMoney = (v) => fmtNum(v, 2);

    const load = async () => {
        setLoading(true);
        try {
            const params = { ...buildFilterParams(filters), breakdown };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;

            const resp = await axios.get('/analytics/wb/statistic/daily-by-entity', { params });
            setRawRows(resp.data.rows || []);
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    // --- Pivot data ---
    const { entities, dates, pivotMap, entityTotals, dateTotals, grandTotal } = useMemo(() => {
        const dateSet = new Set();
        const entityMap = new Map();
        for (const r of rawRows) {
            const entity = r.entity || 'Не указано';
            dateSet.add(r.dt);
            if (!entityMap.has(entity)) entityMap.set(entity, new Map());
            const existing = entityMap.get(entity).get(r.dt);
            if (existing) {
                existing.ordersCount += Number(r.ordersCount || 0);
                existing.unitsCount += Number(r.unitsCount || 0);
                existing.ordersSumRub += Number(r.ordersSumRub || 0);
                existing.profit += Number(r.profit || 0);
            } else {
                entityMap.get(entity).set(r.dt, {
                    ordersCount: Number(r.ordersCount || 0),
                    unitsCount: Number(r.unitsCount || 0),
                    ordersSumRub: Number(r.ordersSumRub || 0),
                    profit: Number(r.profit || 0),
                });
            }
        }
        // recalc avgPrice
        for (const dMap of entityMap.values()) {
            for (const cell of dMap.values()) {
                cell.avgPrice = cell.ordersCount ? +(cell.ordersSumRub / cell.ordersCount).toFixed(2) : 0;
            }
        }
        const dates = [...dateSet].sort();
        // sort entities by total ordersSumRub desc
        const entityArr = [...entityMap.keys()].sort((a, b) => {
            const sumA = [...entityMap.get(a).values()].reduce((s, c) => s + c.ordersSumRub, 0);
            const sumB = [...entityMap.get(b).values()].reduce((s, c) => s + c.ordersSumRub, 0);
            return sumB - sumA;
        });
        // entity totals (row)
        const entityTotals = {};
        for (const e of entityArr) {
            let ordersCount = 0, unitsCount = 0, ordersSumRub = 0, profit = 0;
            for (const cell of entityMap.get(e).values()) {
                ordersCount += cell.ordersCount;
                unitsCount += cell.unitsCount;
                ordersSumRub += cell.ordersSumRub;
                profit += cell.profit;
            }
            entityTotals[e] = { ordersCount, unitsCount, ordersSumRub, avgPrice: ordersCount ? +(ordersSumRub / ordersCount).toFixed(2) : 0, profit };
        }
        // date totals (column)
        const dateTotals = {};
        for (const dt of dates) {
            let ordersCount = 0, unitsCount = 0, ordersSumRub = 0, profit = 0;
            for (const dMap of entityMap.values()) {
                const cell = dMap.get(dt);
                if (cell) { ordersCount += cell.ordersCount; unitsCount += cell.unitsCount; ordersSumRub += cell.ordersSumRub; profit += cell.profit; }
            }
            dateTotals[dt] = { ordersCount, unitsCount, ordersSumRub, avgPrice: ordersCount ? +(ordersSumRub / ordersCount).toFixed(2) : 0, profit };
        }
        // grand total
        let gOrders = 0, gUnits = 0, gSum = 0, gProfit = 0;
        for (const t of Object.values(entityTotals)) { gOrders += t.ordersCount; gUnits += t.unitsCount; gSum += t.ordersSumRub; gProfit += t.profit; }
        const grandTotal = { ordersCount: gOrders, unitsCount: gUnits, ordersSumRub: gSum, avgPrice: gOrders ? +(gSum / gOrders).toFixed(2) : 0, profit: gProfit };

        return { entities: entityArr, dates, pivotMap: entityMap, entityTotals, dateTotals, grandTotal };
    }, [rawRows]);

    // --- Chart data ---
    const chartData = useMemo(() => {
        if (!dates.length) return [];
        return dates.map(dt => {
            const point = { dt: dt.slice(5) };
            if (showTotal) {
                const dtData = dateTotals[dt];
                point['Итого'] = dtData?.[chartMetric] || 0;
            }
            for (const entity of chartEntities) {
                const cell = pivotMap.get(entity)?.get(dt);
                point[entity] = cell ? (cell[chartMetric] || 0) : 0;
            }
            return point;
        });
    }, [dates, dateTotals, pivotMap, chartMetric, chartEntities, showTotal]);

    const chartTooltipFormatter = (value) => {
        if (chartMetric === 'ordersCount' || chartMetric === 'unitsCount') return fmtNum(value, 0);
        return fmtMoney(value);
    };

    // --- Delta helpers ---
    const calcDeltaPct = (curr, prev) => {
        if (prev == null || prev === 0 || curr == null) return null;
        return ((curr - prev) / Math.abs(prev)) * 100;
    };

    const renderMetricLine = (label, value, prevValue, key) => {
        const formatted = (key === 'ordersCount' || key === 'unitsCount') ? fmtNum(value, 0) : fmtMoney(value);
        const lowerBetter = false; // for these metrics higher is generally better (or neutral for avgPrice)
        const isNeutral = key === 'avgPrice';

        if (!showDelta || prevValue == null || value == null) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{label}</Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{formatted}</Typography>
                </Box>
            );
        }

        const deltaPct = calcDeltaPct(Number(value), Number(prevValue));
        const showArrow = deltaPct != null && Number.isFinite(deltaPct) && Math.abs(deltaPct) >= 0.01;

        let arrowColor = 'text.secondary';
        if (showArrow && !isNeutral) {
            const isGood = lowerBetter ? deltaPct < 0 : deltaPct > 0;
            arrowColor = isGood ? 'success.main' : 'error.main';
        }
        if (showArrow && key === 'profit') {
            arrowColor = deltaPct > 0 ? 'success.main' : 'error.main';
        }

        const ArrowIcon = deltaPct > 0 ? ArrowDropUpIcon : ArrowDropDownIcon;

        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{formatted}</Typography>
                    {showArrow && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.25 }}>
                            <ArrowIcon sx={{ color: arrowColor, fontSize: 14, m: -0.25 }} />
                            <Typography variant="caption" sx={{ color: arrowColor, fontSize: '0.6rem', lineHeight: 1 }}>
                                {fmtNum(Math.abs(deltaPct), 1)}%
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderCell = (entity, dtIdx) => {
        const dt = dates[dtIdx];
        const cell = pivotMap.get(entity)?.get(dt);
        if (!cell) return <Box sx={{ color: 'text.disabled', textAlign: 'center', fontSize: '0.7rem' }}>-</Box>;

        const prevDt = dtIdx > 0 ? dates[dtIdx - 1] : null;
        const prevCell = prevDt ? pivotMap.get(entity)?.get(prevDt) : null;

        return (
            <Box sx={{ minWidth: 120 }}>
                {renderMetricLine('Заказы', cell.ordersCount, prevCell?.ordersCount, 'ordersCount')}
                {renderMetricLine('Штуки', cell.unitsCount, prevCell?.unitsCount, 'unitsCount')}
                {renderMetricLine('Ср. цена', cell.avgPrice, prevCell?.avgPrice, 'avgPrice')}
                {renderMetricLine('Профит', cell.profit, prevCell?.profit, 'profit')}
            </Box>
        );
    };

    const renderTotalCell = (data) => (
        <Box sx={{ minWidth: 120 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Заказы</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{fmtNum(data.ordersCount, 0)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Штуки</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{fmtNum(data.unitsCount, 0)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Ср. цена</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{fmtMoney(data.avgPrice)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Профит</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: data.profit >= 0 ? 'success.main' : 'error.main' }}>
                    {fmtMoney(data.profit)}
                </Typography>
            </Box>
        </Box>
    );

    const renderDateTotalCell = (dtIdx) => {
        const dt = dates[dtIdx];
        const data = dateTotals[dt];
        if (!data) return null;

        const prevDt = dtIdx > 0 ? dates[dtIdx - 1] : null;
        const prevData = prevDt ? dateTotals[prevDt] : null;

        return (
            <Box sx={{ minWidth: 120 }}>
                {renderMetricLine('Заказы', data.ordersCount, prevData?.ordersCount, 'ordersCount')}
                {renderMetricLine('Штуки', data.unitsCount, prevData?.unitsCount, 'unitsCount')}
                {renderMetricLine('Ср. цена', data.avgPrice, prevData?.avgPrice, 'avgPrice')}
                {renderMetricLine('Профит', data.profit, prevData?.profit, 'profit')}
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Сводная по дням</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <ProductFilters value={filters} onChange={setFilters} />
                <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    <Grid item xs={6} sm={4} md={1.5}>
                        <TextField select label="Разбивка" value={breakdown} onChange={e => setBreakdown(e.target.value)} size="small" fullWidth>
                            {breakdowns.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8} md={3}>
                        <DateRangeField
                            valueFrom={dateFrom} valueTo={dateTo}
                            onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={1.5}>
                        <Button variant="contained" onClick={load} disabled={loading} fullWidth>
                            {loading ? <CircularProgress size={20} /> : 'Загрузить'}
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControlLabel
                            control={<Checkbox size="small" checked={showDelta} onChange={() => setShowDelta(p => !p)} />}
                            label={<Typography variant="body2">Показывать изменения</Typography>}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {rawRows.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <TextField
                            select label="Показатель" value={chartMetric}
                            onChange={e => setChartMetric(e.target.value)}
                            size="small" sx={{ minWidth: 160 }}
                        >
                            {chartMetrics.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </TextField>
                        <Chip
                            label="Итого"
                            size="small"
                            variant={showTotal ? 'filled' : 'outlined'}
                            color={showTotal ? 'primary' : 'default'}
                            onClick={() => setShowTotal(p => !p)}
                        />
                        {chartEntities.length > 0 && (
                            <Chip label="Сбросить выделение" size="small" variant="outlined" color="default"
                                onClick={() => { setChartEntities([]); setShowTotal(true); }}
                                onDelete={() => { setChartEntities([]); setShowTotal(true); }} />
                        )}
                    </Box>
                    <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                    </linearGradient>
                                    {chartEntities.map((entity, idx) => {
                                        const color = CHART_COLORS[entities.indexOf(entity) % CHART_COLORS.length];
                                        return (
                                            <linearGradient key={entity} id={`grad_${idx}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                                            </linearGradient>
                                        );
                                    })}
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="dt" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                                <Tooltip formatter={chartTooltipFormatter} />
                                <Legend />
                                {showTotal && (
                                    <Area
                                        type="monotone" dataKey="Итого" name="Итого"
                                        stroke="#8884d8" strokeWidth={2}
                                        fill="url(#gradTotal)"
                                    />
                                )}
                                {chartEntities.map((entity, idx) => {
                                    const color = CHART_COLORS[entities.indexOf(entity) % CHART_COLORS.length];
                                    return (
                                        <Area
                                            key={entity} type="monotone" dataKey={entity} name={entity}
                                            stroke={color} strokeWidth={2}
                                            fill={`url(#grad_${idx})`}
                                        />
                                    );
                                })}
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            )}

            {rawRows.length > 0 && (
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...headerStyle, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'grey.100', minWidth: 160 }}>
                                    {breakdowns.find(b => b.value === breakdown)?.label || 'Сущность'}
                                </TableCell>
                                {dates.map(dt => (
                                    <TableCell key={dt} sx={headerStyle} align="center">{dt}</TableCell>
                                ))}
                                <TableCell sx={{ ...headerStyle, fontWeight: 'bold' }} align="center">Итого</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entities.map((entity, eIdx) => (
                                <TableRow key={entity} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                                    <TableCell
                                        onClick={() => toggleChartEntity(entity)}
                                        sx={{
                                            ...cellStyle,
                                            position: 'sticky', left: 0, zIndex: 1,
                                            bgcolor: chartEntities.includes(entity)
                                                ? '#eef' : (eIdx % 2 === 0 ? '#fafafa' : '#fff'),
                                            fontWeight: 600, borderRight: '1px solid', borderRightColor: 'divider',
                                            cursor: 'pointer', userSelect: 'none',
                                            borderLeft: chartEntities.includes(entity)
                                                ? `3px solid ${CHART_COLORS[entities.indexOf(entity) % CHART_COLORS.length]}`
                                                : 'none',
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }} noWrap title={entity}>
                                            {entity}
                                        </Typography>
                                    </TableCell>
                                    {dates.map((dt, dtIdx) => (
                                        <TableCell key={dt} sx={cellStyle} align="right">
                                            {renderCell(entity, dtIdx)}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ ...cellStyle, bgcolor: 'grey.50', borderLeft: '2px solid', borderLeftColor: 'divider' }} align="right">
                                        {entityTotals[entity] && renderTotalCell(entityTotals[entity])}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {/* Totals row */}
                            <TableRow sx={{ bgcolor: 'grey.200' }}>
                                <TableCell sx={{
                                    ...cellStyle,
                                    position: 'sticky', left: 0, zIndex: 1,
                                    bgcolor: 'grey.200', fontWeight: 'bold',
                                    borderRight: '1px solid', borderRightColor: 'divider',
                                    borderTop: '2px solid', borderTopColor: 'divider',
                                }}>
                                    Итого
                                </TableCell>
                                {dates.map((dt, dtIdx) => (
                                    <TableCell key={dt} sx={{ ...cellStyle, bgcolor: 'grey.200', borderTop: '2px solid', borderTopColor: 'divider' }} align="right">
                                        {renderDateTotalCell(dtIdx)}
                                    </TableCell>
                                ))}
                                <TableCell sx={{
                                    ...cellStyle, bgcolor: 'grey.300', fontWeight: 'bold',
                                    borderLeft: '2px solid', borderLeftColor: 'divider',
                                    borderTop: '2px solid', borderTopColor: 'divider',
                                }} align="right">
                                    {renderTotalCell(grandTotal)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && rawRows.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Нажмите «Загрузить» для получения данных</Typography>
                </Paper>
            )}
        </Box>
    );
}
